import { type Lote } from '@fiscoai/contracts';

/** Nome curto da planilha (cabe numa linha do histórico; o nome longo fica na tela do lote). */
const PLANILHA_CURTA = {
  SEFAZ_NFE_DESTINADAS: 'Relatório da SEFAZ',
  GENERICA: 'Planilha de chaves',
} as const;

/** Nome do envio como a pessoa reconhece: o relatório, ou quantos arquivos foram. */
export function tituloDoLote(lote: Pick<Lote, 'formatoPlanilha' | 'totalItens'>): string {
  if (lote.formatoPlanilha) return PLANILHA_CURTA[lote.formatoPlanilha];
  return lote.totalItens === 1 ? '1 arquivo' : `${String(lote.totalItens)} arquivos`;
}

export interface ResumoLote {
  /** Notas que entraram (novas ou atualizadas). */
  entraram: number;
  /** Chaves conhecidas pelo relatório que ainda esperam o XML. */
  aguardando: number;
  /** Layout que o FiscoAI ainda não lê (ex.: NFS-e) — informativo, não é erro do envio. */
  naoSuportados: number;
  /** O que precisa de atenção: inválidos, erros e notas sem empresa do grupo. */
  problemas: number;
}

export function resumoDoLote(lote: Pick<Lote, 'contadores'>): ResumoLote {
  const c = (k: keyof Lote['contadores']) => lote.contadores[k];
  return {
    entraram: c('IMPORTADO') + c('ATUALIZADO'),
    aguardando: c('CHAVE_PENDENTE'),
    naoSuportados: c('NAO_SUPORTADO'),
    problemas: c('INVALIDO') + c('ERRO') + c('SEM_EMPRESA'),
  };
}

/** Frase curta do resultado ("12 notas novas, 1 com problema"); vazio enquanto processa. */
export function textoDoResumo(r: ResumoLote): string {
  const partes: string[] = [];
  if (r.entraram > 0)
    partes.push(r.entraram === 1 ? '1 nota nova' : `${String(r.entraram)} notas novas`);
  if (r.aguardando > 0)
    partes.push(r.aguardando === 1 ? '1 aguardando XML' : `${String(r.aguardando)} aguardando XML`);
  if (r.naoSuportados > 0)
    partes.push(
      r.naoSuportados === 1 ? '1 não suportado' : `${String(r.naoSuportados)} não suportados`,
    );
  if (r.problemas > 0)
    partes.push(r.problemas === 1 ? '1 com problema' : `${String(r.problemas)} com problema`);
  return partes.length > 0 ? partes.join(', ') : 'Nada novo';
}

const DIA_MS = 86_400_000;
const diaLocal = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

/** "Hoje", "Ontem" ou a data (dd/mm/aaaa) — cabeçalho dos grupos do histórico. */
export function rotuloDoDia(iso: string, agora = new Date()): string {
  const quando = new Date(iso);
  const dias = Math.round((diaLocal(agora) - diaLocal(quando)) / DIA_MS);
  if (dias === 0) return 'Hoje';
  if (dias === 1) return 'Ontem';
  return quando.toLocaleDateString('pt-BR');
}

/** Agrupa preservando a ordem (lista já vem do mais novo para o mais antigo). */
export function agruparPorDia<T extends { criadoEm: string }>(
  lotes: readonly T[],
  agora = new Date(),
): { dia: string; lotes: T[] }[] {
  const grupos: { dia: string; lotes: T[] }[] = [];
  for (const lote of lotes) {
    const dia = rotuloDoDia(lote.criadoEm, agora);
    const ultimo = grupos.at(-1);
    if (ultimo?.dia === dia) ultimo.lotes.push(lote);
    else grupos.push({ dia, lotes: [lote] });
  }
  return grupos;
}
